import { useId, useState } from "react";
import { ChevronDown, Loader2, X } from "lucide-react";
import {
  LINK_FOUND_BY_LABELS,
  LINK_KIND_PHRASES,
  MADE_BY_LABELS,
  type ContainedFile,
  type DocumentLink,
  type LinkedFile,
} from "@gb/contracts";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FileLink, type CameFrom } from "./file-link";
import { useDocumentLinks, useSetLinkState } from "./links-api";

/**
 * The files this one is connected to (`document-ai/0024`, Phase 4).
 *
 * Shown to everyone, because a person can remove a wrong link and answer an
 * unsure one; the machinery behind each — evidence, removed links, the model
 * call — is the debug view's. **Renders nothing when there are none:** most
 * files have no links, and an empty box on every page would read as a gap.
 *
 * **Folded, and closed by default** (the product owner, 2026-09-27, `0024`
 * §20). Open, each asked link is a large card, and a file with several pushed
 * everything after them down the page. The summary line says only "Linked
 * files to verify" when a link waits for an answer, in the warning colour,
 * and "Linked files" when none does (the owner, the same day: one row, a
 * generic line to catch the eye, no counts). A question is folded away but
 * never hidden. `<details>`, as `SourceGroupBlock` is: it stays open across the
 * re-render an answer causes without anything holding that. The review after
 * an import shows the same questions unfolded, because there they are the task.
 *
 * Each link is said from this file's side, with the other file as a link to
 * its own page, and where following it lands when that is a newer version.
 * What the file's container says comes with the links (`containment`,
 * 2026-10-18): the file it was unpacked from and the files unpacked from it,
 * said the same way but with nothing to remove, since it is read from the
 * container and is not a judgement (`document-ai/0024` §8.4).
 */
export function DocumentLinks({
  projectId,
  documentId,
  from,
}: {
  projectId: string;
  documentId: string;
  from?: CameFrom;
}) {
  const links = useDocumentLinks(projectId, documentId);
  const asked = links.data?.links.filter((link) => link.state === "asked") ?? [];
  const live = links.data?.links.filter((link) => link.state === "live") ?? [];
  const containment = links.data?.containment ?? null;
  const contained = (containment?.container ? 1 : 0) + (containment?.contents.length ?? 0);
  const total = asked.length + live.length + contained;
  if (total === 0) return null;

  return (
    <details className="group mt-3 max-w-[60ch]">
      <summary
        className={cn(
          "flex cursor-pointer list-none items-center gap-1.5 py-1",
          "font-display text-[0.66rem] font-semibold tracking-[0.12em] text-muted uppercase",
          "hover:text-ink",
        )}
      >
        <ChevronDown
          size={13}
          aria-hidden
          className="shrink-0 -rotate-90 transition-transform group-open:rotate-0"
        />
        {asked.length > 0 ? (
          <span className="text-warn-text">Linked files to verify</span>
        ) : (
          <span>Linked files</span>
        )}
      </summary>
      {asked.map((link) => (
        <AskedLink key={link.id} projectId={projectId} link={link} from={from} />
      ))}
      {live.length + contained > 0 && (
        <ul className="mt-1.5 flex flex-col gap-1.5">
          {containment?.container && (
            <ContainmentRow projectId={projectId} file={containment.container} side="container" from={from} />
          )}
          {containment?.contents.map((file) => (
            <ContainmentRow key={file.id} projectId={projectId} file={file} side="content" from={from} />
          ))}
          {live.map((link) => (
            <LinkRow key={link.id} projectId={projectId} link={link} from={from} />
          ))}
        </ul>
      )}
    </details>
  );
}

/**
 * "“Drawing pack” contains this file", or "Contains “plan.pdf”": one side of
 * the file's containment, with no Remove, since a container is a fact.
 */
function ContainmentRow({
  projectId,
  file,
  side,
  from,
}: {
  projectId: string;
  file: ContainedFile;
  side: "container" | "content";
  from?: CameFrom;
}) {
  return (
    <li className="text-[0.86rem] text-ink">
      <p className="min-w-0 break-words">
        {side === "container" ? (
          <>
            “<Named projectId={projectId} file={file} from={from} />” contains this file
          </>
        ) : (
          <>
            Contains “<Named projectId={projectId} file={file} from={from} />”
          </>
        )}
      </p>
      <p className="text-[0.76rem] text-muted">
        Unpacked from it · {MADE_BY_LABELS.code}
        {file.path ? ` · ${file.path}` : ""}
      </p>
    </li>
  );
}

/** One live link, said from this file's side, with Remove for one that can be. */
function LinkRow({ projectId, link, from }: { projectId: string; link: DocumentLink; from?: CameFrom }) {
  const [confirming, setConfirming] = useState(false);
  const setState = useSetLinkState(projectId);
  return (
    <li className="text-[0.86rem] text-ink">
      {/* The sentence takes the spare width and wraps inside it; the button
          and the confirm step keep their size, so they stay on its row
          rather than dropping, alone and unlabelled, onto a line below. */}
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 flex-1 break-words">
          <LinkSentence projectId={projectId} link={link} from={from} />
        </p>
        {confirming ? (
            <span className="flex shrink-0 items-center gap-1.5">
              <span className="text-[0.78rem] font-semibold whitespace-nowrap text-ink">Remove this link?</span>
              <Button variant="secondary" size="sm" disabled={setState.isPending} onClick={() => setConfirming(false)}>
                No
              </Button>
              <Button
                size="sm"
                className="hover:bg-brand-deep"
                disabled={setState.isPending}
                onClick={() =>
                  setState.mutate(
                    { linkId: link.id, state: "removed" },
                    { onSettled: () => setConfirming(false) },
                  )
                }
              >
                Yes
              </Button>
            </span>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={() => setConfirming(true)}
              aria-label="Remove this link"
            >
              <X size={14} />
            </Button>
          )}
      </div>
      <p className="text-[0.76rem] text-muted">
        {LINK_FOUND_BY_LABELS[link.foundBy]} · {MADE_BY_LABELS[link.madeBy]}
        {link.reason ? ` · ${link.reason}` : ""}
      </p>
      {setState.error && (
        <p role="alert" className="text-[0.8rem] font-semibold text-danger">
          {setState.error.message}
        </p>
      )}
    </li>
  );
}

/** A link the model was not sure of, asked here with Yes and No. */
function AskedLink({ projectId, link, from }: { projectId: string; link: DocumentLink; from?: CameFrom }) {
  const setState = useSetLinkState(projectId);
  return (
    <div className="mt-1.5">
      <LinkQuestion
        projectId={projectId}
        link={link}
        from={from}
        busy={setState.isPending}
        onAnswer={(yes) => setState.mutate({ linkId: link.id, state: yes ? "live" : "removed" })}
      />
      {setState.error && (
        <p role="alert" className="mt-1 text-[0.8rem] font-semibold text-danger">
          {setState.error.message}
        </p>
      )}
    </div>
  );
}

/**
 * The model's question about a link, and Yes or No. Shared by the file's page
 * and the review after an import, as a placement's question is, so a person
 * reads the same words in both. Presentational: the caller owns the answer.
 */
export function LinkQuestion({
  projectId,
  link,
  busy,
  onAnswer,
  from,
}: {
  projectId: string;
  link: DocumentLink;
  busy: boolean;
  onAnswer: (yes: boolean) => void;
  from?: CameFrom;
}) {
  const ids = useId();
  return (
    <div
      role="group"
      aria-labelledby={`${ids}-question`}
      className="rounded-card-lg border-[1.5px] border-warn/40 bg-warn-bg px-4 py-3"
    >
      <p className="font-display text-[0.66rem] font-semibold tracking-[0.12em] text-warn-text uppercase">
        Is this link right?
      </p>
      <p id={`${ids}-question`} className="mt-1 text-[0.92rem] font-semibold text-ink">
        “<Named projectId={projectId} file={link.from} from={from} />” {LINK_KIND_PHRASES[link.kind].out} “
        <Named projectId={projectId} file={link.to} from={from} />”
      </p>
      <p className="mt-1 text-[0.82rem] leading-[1.5] text-ink-slate">
        The model wasn’t sure, so nothing follows this link until someone answers.
      </p>
      {link.reason && (
        <p className="mt-1 text-[0.82rem] leading-[1.5] text-ink-slate">The model’s reason: {link.reason}</p>
      )}
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <Button size="sm" className="hover:bg-brand-deep" disabled={busy} onClick={() => onAnswer(true)}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          Yes
        </Button>
        <Button variant="secondary" size="sm" disabled={busy} onClick={() => onAnswer(false)}>
          No
        </Button>
      </div>
    </div>
  );
}

/**
 * "refers to “Purchase order”", or "“RE: Order” responds to this file". The
 * other file is always the linked one; the kind is always said forwards, which
 * reads better than its inverse ("is referred to by").
 */
export function LinkSentence({
  projectId,
  link,
  from,
}: {
  projectId: string;
  link: DocumentLink;
  from?: CameFrom;
}) {
  const phrase = LINK_KIND_PHRASES[link.kind].out;
  if (link.direction === "in") {
    return (
      <>
        “<Named projectId={projectId} file={link.from} from={from} />” {phrase} this file
      </>
    );
  }
  return (
    <>
      {phrase.charAt(0).toUpperCase() + phrase.slice(1)} “<Named projectId={projectId} file={link.to} from={from} />”
      {link.current && (
        <span className="text-muted">
          {" "}
          — now “
          <FileLink projectId={projectId} id={link.current.id} from={from}>
            {link.current.name}
          </FileLink>
          ”, its current version
        </span>
      )}
    </>
  );
}

/** A file's name, as a link to its page while it exists. */
function Named({ projectId, file, from }: { projectId: string; file: LinkedFile; from?: CameFrom }) {
  if (!file.exists) return <span>{file.name} (deleted)</span>;
  return (
    <FileLink projectId={projectId} id={file.id} from={from}>
      {file.name}
    </FileLink>
  );
}
